import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { playerHandleSuffix } from "@bw/shared/components";
import {
  formatCourtLineSummary,
  formatCourtStatusAnnouncement,
  getCourtStateLabel,
} from "@bw/shared/utils";
import type {
  CourtBoardEntry,
  CourtBoardLineGroup,
  CourtBoardPresence,
  PartyCourtPlacement,
  PartyEligibilityResponse,
  QueueEntryResponse,
} from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import { DetailHeader } from "../../../common/components/DetailHeader";
import { visuallyHidden } from "../../../common/visuallyHidden";
import { useCourtBoard } from "../hooks/useCourtBoard";
import { useFacilityMode } from "../hooks/useFacilityMode";
import { useDropInSession } from "../hooks/useDropInSession";
import { useJoinCourt } from "../hooks/useJoinCourt";
import { useLeaveCourt } from "../hooks/useLeaveCourt";
import { useJoinLine } from "../hooks/useJoinLine";
import {
  activeDropInAuthorizationExpiresAt,
  dropInSessionSnapshotIsAuthoritative,
} from "../lib/activeDropInAuthorization";
import { useLeaveLine } from "../hooks/useLeaveLine";
import { useMyQueueEntry } from "../hooks/useMyQueueEntry";
import { useMyParty } from "../hooks/useMyParty";
import { useQueueParty } from "../hooks/useQueueParty";
import { useKickPartyMember } from "../hooks/useKickPartyMember";
import {
  createEligibilityFocusRequest,
  hasPartyEligibilityDisplayChange,
  PartyEligibilityPanel,
  type EligibilityFocusRequest,
} from "../components/PartyEligibilityPanel";
import {
  partyPlacementConflictNotice,
  type PartyPlacementNotice,
} from "../lib/partyPlacementConflict";

/** The em-dash shown for an unresolved player name (the no-UID-in-UI fence). */
const EM_DASH = "—";

/**
 * Honest copy for a solo-can't-start 409. The backend forbids a lone player
 * claiming an empty court (SoloCannotStartCourtError); tell the player how to
 * proceed instead of surfacing a raw error / status code.
 */
const SOLO_CANNOT_START_COPY =
  "A new court needs at least 2 players. Join the line or a game in progress.";

/** Generic, honest join-failure copy (any non-409 error). */
const JOIN_FAILED_COPY = "We couldn't join that court. Please try again.";

/** Honest, retryable copy for a failed Leave (the backend rejected the leave). */
const LEAVE_FAILED_COPY =
  "We couldn't take you off the court. Please try again.";

const LEAVE_LINE_FAILED_COPY =
  "We couldn't remove you from that line. Please try again.";

/**
 * Honest copy for the "one spot anywhere" 409 on Join line — the caller
 * already holds an active facility queue entry, a line for this or another
 * court, or a court assignment somewhere at this facility
 * (AlreadyInQueueError, code ALREADY_IN_QUEUE).
 */
const ALREADY_IN_LINE_COPY =
  "You already have a spot in a queue or line at this facility.";

/** Generic, honest join-line-failure copy for errors without a specific
 * recovery path — self-serve disabled, no active session, court not found,
 * network, 5xx, etc. */
const JOIN_LINE_FAILED_COPY =
  "We couldn't add you to that line. Please try again.";
const FACILITY_CLOSING_COPY =
  "There isn't enough drop-in time to join that line. Try again when play resumes.";

/**
 * Map a join failure to honest inline copy. Only a SOLO_CANNOT_START 409 gets
 * the "need a party" guidance; any other failure (other 409s, 403, 500, network)
 * gets the generic retry message. Never the raw error or "409".
 *
 * The shape is duck-typed (`code`) rather than gated on `isApiError`: the real
 * httpClient throws an `ApiError` carrying `code`, but a caller (or a test seam)
 * may surface a plain Error decorated with the same field — both must map to the
 * honest copy, never leak the raw message.
 *
 * The gate keys on `code === "SOLO_CANNOT_START"` ONLY. The backend now emits
 * `_make_structured_handler(409, "SOLO_CANNOT_START")` (#1524), so the FE
 * httpClient surfaces the real solo-can't-start error with a stable code instead
 * of falling back to `UNKNOWN_ERROR`. Any other 409 (ALREADY_ON_COURT,
 * COURT_CLOSED, etc.) gets the generic retry copy, not the party guidance.
 */
function mapJoinError(err: unknown): string {
  const e = err as { status?: number; code?: string } | null;
  if (e && e.code === "SOLO_CANNOT_START") {
    return SOLO_CANNOT_START_COPY;
  }
  return JOIN_FAILED_COPY;
}

/**
 * Map a leave failure to honest, retryable copy. A failed Leave must NEVER be
 * silently swallowed — the player could believe they left while the server still
 * has them on the court. The raw error is never shown.
 */
function mapLeaveError(_err: unknown): string {
  return LEAVE_FAILED_COPY;
}

/**
 * Map a join-line failure to honest inline copy. Stable ALREADY_IN_QUEUE and
 * FACILITY_CLOSING conflicts get specific recovery copy; every other failure
 * gets the generic retry message. Never expose the raw error, message, or
 * status code.
 */
function mapJoinLineError(err: unknown): string {
  const e = err as { status?: number; code?: string } | null;
  if (e && e.code === "ALREADY_IN_QUEUE") {
    return ALREADY_IN_LINE_COPY;
  }
  if (e && e.code === "FACILITY_CLOSING") {
    return FACILITY_CLOSING_COPY;
  }
  return JOIN_LINE_FAILED_COPY;
}

/** Generic, honest queue-party-failure copy (any error other than the
 * one-spot-anywhere 409 — a member's session lapsed, capacity, network, 5xx,
 * etc.). */
const QUEUE_PARTY_FAILED_COPY =
  "We couldn't queue the party for that court. Please try again.";

/**
 * Map a "Queue party" failure to honest inline copy (ADR-025 Phase D
 * party-lines, #64). Reuses the SAME structured ALREADY_IN_QUEUE 409 copy as
 * solo Join line — the backend's one-spot-anywhere rule is identical for a
 * party. Canonical 409 reconciliation is handled by the page before this mapper;
 * any remaining failure gets the generic retry message, never the raw error,
 * message, or status code.
 */
function mapQueuePartyError(err: unknown): string {
  const e = err as { status?: number; code?: string } | null;
  if (e && e.code === "ALREADY_IN_QUEUE") {
    return ALREADY_IN_LINE_COPY;
  }
  return QUEUE_PARTY_FAILED_COPY;
}

function safeEligibilityMemberName(
  eligibility: PartyEligibilityResponse,
  memberId: string,
): string | null {
  const member = (eligibility.members ?? []).find(
    (candidate) => candidate.member_id === memberId,
  );
  const name = member?.display_name?.trim();
  return !member || !name || name === member.member_id.trim() ? null : name;
}

function blockingMemberLabel(
  placement: PartyCourtPlacement,
  eligibility: PartyEligibilityResponse,
): string {
  const names = (placement.blocking_member_ids ?? [])
    .map((memberId) => safeEligibilityMemberName(eligibility, memberId))
    .filter((name): name is string => name !== null);
  if (names.length === 0) return "A party member";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]}, ${names[1]}, and ${names.length - 2} others`;
}

function CourtPartyBlocker({
  placement,
  eligibility,
}: {
  placement: PartyCourtPlacement | null | undefined;
  eligibility: PartyEligibilityResponse;
}) {
  if (placement?.can_place) return null;

  let memberLabel: string | null = null;
  let copy: string;
  switch (placement?.reason) {
    case "party_blocked":
      memberLabel = blockingMemberLabel(placement, eligibility);
      copy = " needs action before this party can play here.";
      break;
    case "queue_closed":
      copy = "Court placement is paused.";
      break;
    case "court_maintenance":
      copy = "Court is under maintenance.";
      break;
    case "court_closed":
      copy = "Court is closed.";
      break;
    case "court_reserved":
      copy = "Court is reserved for another activity.";
      break;
    case "party_too_large":
      copy = "This party does not fit this court's capacity.";
      break;
    case "facility_closing":
      copy = "Not enough drop-in time remains.";
      break;
    case "game_end_unknown":
      copy = "The next game start time is unavailable.";
      break;
    case "member_session_too_short":
      memberLabel = blockingMemberLabel(placement, eligibility);
      copy = " has a session that ends too soon for the next game.";
      break;
    default:
      copy = "Party placement status is unavailable.";
      break;
  }

  return (
    <Typography variant="body2" color="warning.main">
      {memberLabel ? <Box component="strong">{memberLabel}</Box> : null}
      {copy}
    </Typography>
  );
}

/** Primary-state tint follows operation first, then physical occupancy. */
function stateBadgeColor(
  court: Pick<CourtBoardEntry, "operational_state" | "occupancy_state">,
): "warning" | "primary" | "success" {
  if (court.operational_state !== "operational") {
    return "warning";
  }
  if (court.occupancy_state === "in_play") {
    return "primary";
  }
  return court.occupancy_state === "reserved" ? "warning" : "success";
}

/**
 * The backend supplies an opaque board-local presence key. A resolved name
 * renders with its short presence marker so duplicate names remain distinguishable
 * without exposing a user, session, queue-entry, or assignment identifier.
 */
function PlayerName({ player }: { player: CourtBoardPresence }) {
  const name = player.display_name?.trim();
  const resolved = name && name !== player.presence_id.trim() ? name : null;

  if (!resolved) {
    return <Typography component="span">{EM_DASH}</Typography>;
  }
  return (
    <Stack component="span" direction="row" spacing={0.75}>
      <Typography component="span">{resolved}</Typography>
      <Typography component="span" color="text.secondary">
        {playerHandleSuffix(player.presence_id)}
      </Typography>
    </Stack>
  );
}

function partyEligibilitySnapshotsAgree(
  boardEligibility: PartyEligibilityResponse,
  partyEligibility: PartyEligibilityResponse,
): boolean {
  const boardMembers = boardEligibility.members;
  const partyMembers = partyEligibility.members;
  if (
    !Array.isArray(boardMembers) ||
    !Array.isArray(partyMembers) ||
    boardEligibility.revision !== partyEligibility.revision ||
    boardEligibility.party_id !== partyEligibility.party_id ||
    boardEligibility.facility_id !== partyEligibility.facility_id ||
    boardEligibility.assignment_mode !== partyEligibility.assignment_mode ||
    boardEligibility.state !== partyEligibility.state ||
    boardEligibility.reason !== partyEligibility.reason ||
    boardEligibility.leader_can_place !== partyEligibility.leader_can_place ||
    boardEligibility.action !== partyEligibility.action ||
    boardMembers.length !== partyMembers.length
  ) {
    return false;
  }

  const partyMembersById = new Map(
    partyMembers.map((member) => [member.member_id, member]),
  );
  return boardMembers.every((boardMember) => {
    const partyMember = partyMembersById.get(boardMember.member_id);
    return (
      partyMember !== undefined &&
      boardMember.is_current_user === partyMember.is_current_user &&
      boardMember.admission_state === partyMember.admission_state &&
      boardMember.activity_state === partyMember.activity_state &&
      boardMember.state === partyMember.state &&
      boardMember.reason === partyMember.reason &&
      boardMember.remediation === partyMember.remediation &&
      boardMember.session_expires_at === partyMember.session_expires_at
    );
  });
}

function partyCourtPlacementMatches(
  previous: PartyCourtPlacement | null | undefined,
  next: PartyCourtPlacement | null | undefined,
): boolean {
  if (!previous || !next) return previous === next;
  const previousBlockers = previous.blocking_member_ids ?? [];
  const nextBlockers = next.blocking_member_ids ?? [];
  return (
    previous.can_place === next.can_place &&
    previous.reason === next.reason &&
    previous.required_until === next.required_until &&
    previousBlockers.length === nextBlockers.length &&
    previousBlockers.every(
      (memberId, index) => memberId === nextBlockers[index],
    )
  );
}

function courtPlacementStateChanged(
  previous: CourtBoardEntry | undefined,
  next: CourtBoardEntry | undefined,
): boolean {
  if (!previous || !next) return previous !== next;
  return (
    previous.operational_state !== next.operational_state ||
    previous.occupancy_state !== next.occupancy_state ||
    previous.availability_state !== next.availability_state ||
    previous.can_join_line !== next.can_join_line ||
    !partyCourtPlacementMatches(previous.party_placement, next.party_placement)
  );
}

function groupLabel(group: CourtBoardLineGroup): string {
  return group.player_count === 1 ? "Solo" : `Party of ${group.player_count}`;
}

function groupMeta(group: CourtBoardLineGroup): string {
  const parts = [`Position ${group.position}`];
  if (group.estimated_wait_minutes != null) {
    parts.push(`${group.estimated_wait_minutes} min estimate`);
  }
  return parts.join(" · ");
}

function CourtDetailsSheet({
  court,
  onClose,
}: {
  court: CourtBoardEntry;
  onClose: () => void;
}) {
  const players = court.players ?? [];
  const lineGroups = court.line_groups ?? [];
  const omittedGroups = Math.max(0, court.line_group_count - lineGroups.length);
  const dialogId = `court-details-${court.court_id}`;
  const titleId = `court-details-title-${court.court_id}`;

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      aria-labelledby={titleId}
      slotProps={{ paper: { id: dialogId } }}
    >
      <DialogTitle id={titleId}>{court.name} details</DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Box component="section" aria-labelledby={`${titleId}-playing`}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "baseline", justifyContent: "space-between" }}
            >
              <Typography component="h3" variant="h6" id={`${titleId}-playing`}>
                Playing now
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {players.length}/{court.capacity}
              </Typography>
            </Stack>
            {players.length > 0 ? (
              <Stack
                component="ul"
                spacing={0.75}
                sx={{ mt: 1, p: 0, listStyle: "none" }}
              >
                {players.map((player) => (
                  <Box component="li" key={player.presence_id}>
                    <PlayerName player={player} />
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                No game in progress.
              </Typography>
            )}
          </Box>

          <Box component="section" aria-labelledby={`${titleId}-waiting`}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "baseline", justifyContent: "space-between" }}
            >
              <Typography component="h3" variant="h6" id={`${titleId}-waiting`}>
                Waiting
              </Typography>
              {court.line_group_count > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {court.line_group_count}{" "}
                  {court.line_group_count === 1 ? "party" : "parties"}
                </Typography>
              ) : null}
            </Stack>
            {lineGroups.length > 0 ? (
              <Stack component="ol" spacing={1} sx={{ mt: 1, pl: 3 }}>
                {lineGroups.map((group) => {
                  const isNextUp =
                    court.next_up_group?.line_group_key ===
                    group.line_group_key;
                  return (
                    <Box component="li" key={group.line_group_key}>
                      <Stack spacing={0.5}>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography sx={{ fontWeight: 700 }}>
                            {groupLabel(group)}
                          </Typography>
                          <Stack direction="row" spacing={0.5}>
                            {isNextUp ? (
                              <Chip
                                size="small"
                                color="success"
                                label="Next up"
                              />
                            ) : null}
                            {group.is_current_group ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label="Your group"
                              />
                            ) : null}
                          </Stack>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {groupMeta(group)}
                        </Typography>
                        <Typography
                          component="time"
                          variant="caption"
                          color="text.secondary"
                          dateTime={group.joined_at}
                        >
                          Joined{" "}
                          {new Intl.DateTimeFormat(undefined, {
                            hour: "numeric",
                            minute: "2-digit",
                          }).format(new Date(group.joined_at))}
                        </Typography>
                        <Stack
                          component="ul"
                          spacing={0.5}
                          sx={{ p: 0, listStyle: "none" }}
                        >
                          {group.members.map((member) => (
                            <Box component="li" key={member.presence_id}>
                              <PlayerName player={member} />
                            </Box>
                          ))}
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            ) : (
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                No parties waiting.
              </Typography>
            )}
            {omittedGroups > 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                +{omittedGroups} more{" "}
                {omittedGroups === 1 ? "party" : "parties"}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} autoFocus>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * A single court card: name, canonical primary state, players (Name#abcd), the
 * waiting line, live capacity/privacy detail, and the caller-appropriate action.
 * The action is decided from the backend's availability verdict plus whether the
 * caller is in the players/line lists. The FE never derives availability from
 * capacity or privacy and never pre-computes party eligibility.
 */
function CourtCard({
  court,
  callerLineEntry,
  onJoin,
  onLeave,
  onLeaveLine,
  onJoinLine,
  joinPending,
  leavePending,
  leaveLinePending,
  joinLinePending,
  partyEligibility,
  partyActionEnabled,
  onQueueParty,
  queuePartyPending,
  actionsDisabled,
  statusUnavailable,
  canLeaveLine,
  onCourtRef,
  suppressSoloPlacement,
  soloPlacementDisabled,
}: {
  court: CourtBoardEntry;
  callerLineEntry: QueueEntryResponse | null;
  onJoin: (courtId: string) => void;
  onLeave: (courtId: string) => void;
  onLeaveLine: (entryId: string) => void;
  onJoinLine: (courtId: string) => void;
  joinPending: boolean;
  leavePending: boolean;
  leaveLinePending: boolean;
  joinLinePending: boolean;
  partyEligibility: PartyEligibilityResponse | null;
  partyActionEnabled: boolean;
  onQueueParty: (courtId: string) => void;
  queuePartyPending: boolean;
  actionsDisabled: boolean;
  statusUnavailable: boolean;
  canLeaveLine: boolean;
  onCourtRef: (element: HTMLDivElement | null) => void;
  suppressSoloPlacement: boolean;
  soloPlacementDisabled: boolean;
}) {
  const players = court.players ?? [];
  const lineGroups = court.line_groups ?? [];
  const [detailsOpen, setDetailsOpen] = useState(false);
  const detailsButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (statusUnavailable) setDetailsOpen(false);
  }, [statusUnavailable]);
  const closeDetails = () => {
    detailsButtonRef.current?.focus();
    setDetailsOpen(false);
  };
  const callerOnCourt = players.some((player) => player.is_current_user);
  const lineSummary = formatCourtLineSummary(
    court.line_group_count,
    court.line_player_count,
  );
  const recoveredLineEntry =
    callerLineEntry?.status === "waiting" &&
    callerLineEntry.target_court_id === court.court_id
      ? callerLineEntry
      : null;
  const callerHasActiveEntry =
    callerLineEntry?.status === "waiting" ||
    callerLineEntry?.status === "assigned";
  const callerBlockedByActiveEntry =
    callerHasActiveEntry && !callerOnCourt && recoveredLineEntry === null;
  const callerInLine =
    recoveredLineEntry !== null ||
    lineGroups.some((group) => group.is_current_group);

  const offersLine = court.can_join_line;
  const offersDirectJoin =
    court.availability_state === "joinable" && court.line_group_count === 0;
  const offersPlacement =
    court.operational_state === "operational" &&
    (offersLine || offersDirectJoin);

  // Queue's typed court verdict is the sole party-placement gate. Occupancy is
  // used only to label the one mutation as immediate play versus joining line.
  const showQueueParty =
    court.party_placement?.can_place === true && partyActionEnabled;
  const partyCanPlayNow =
    showQueueParty &&
    court.occupancy_state === "available" &&
    court.line_group_count === 0;

  // The line owns fresh-court starts and waitlists. Direct Join is available only
  // when the backend marks a line-free live game joinable.
  let action: React.ReactNode = null;
  if (callerOnCourt) {
    action = (
      <Button
        type="button"
        variant="outlined"
        size="small"
        onClick={() => onLeave(court.court_id)}
        disabled={actionsDisabled || leavePending}
      >
        Leave
      </Button>
    );
  } else if (recoveredLineEntry && canLeaveLine) {
    action = (
      <Button
        type="button"
        variant="outlined"
        size="small"
        onClick={() => onLeaveLine(recoveredLineEntry.entry_id)}
        disabled={actionsDisabled || leaveLinePending}
      >
        Leave line
      </Button>
    );
  } else if (callerBlockedByActiveEntry) {
    action = null;
  } else if (showQueueParty) {
    const accessibleAction = partyCanPlayNow
      ? `Play here on ${court.name}`
      : `Join court line on ${court.name}`;
    action = (
      <Button
        type="button"
        variant={partyCanPlayNow ? "contained" : "outlined"}
        size="small"
        aria-label={accessibleAction}
        onClick={() => onQueueParty(court.court_id)}
        disabled={actionsDisabled || queuePartyPending}
      >
        {partyCanPlayNow ? "Play here" : "Join court line"}
      </Button>
    );
  } else if (
    offersPlacement &&
    !suppressSoloPlacement &&
    !callerInLine &&
    offersLine &&
    !offersDirectJoin
  ) {
    action = (
      <Button
        type="button"
        variant="outlined"
        size="small"
        onClick={() => onJoinLine(court.court_id)}
        disabled={actionsDisabled || soloPlacementDisabled || joinLinePending}
      >
        Join line
      </Button>
    );
  } else if (offersPlacement && !suppressSoloPlacement && offersDirectJoin) {
    action = (
      <Button
        type="button"
        variant="contained"
        size="small"
        onClick={() => onJoin(court.court_id)}
        disabled={actionsDisabled || soloPlacementDisabled || joinPending}
      >
        Join
      </Button>
    );
  }
  // Unavailable courts and preview-only line members expose no mutation.

  return (
    <Paper
      ref={onCourtRef}
      variant="outlined"
      role="group"
      tabIndex={-1}
      aria-label={
        statusUnavailable
          ? `${court.name}, Unavailable`
          : formatCourtStatusAnnouncement({
              name: court.name,
              operationalState: court.operational_state,
              occupancyState: court.occupancy_state,
              playerCount: players.length,
              capacity: court.capacity,
              availableSlots: court.available_slots,
              isPrivate: court.is_private,
              lineGroupCount: court.line_group_count,
              linePlayerCount: court.line_player_count,
            })
      }
      sx={{ p: 2 }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <Typography sx={{ fontWeight: 800 }}>{court.name}</Typography>
          <Chip
            size="small"
            variant="outlined"
            color={statusUnavailable ? "warning" : stateBadgeColor(court)}
            label={
              statusUnavailable
                ? "Status unavailable"
                : getCourtStateLabel({
                    operationalState: court.operational_state,
                    occupancyState: court.occupancy_state,
                  })
            }
          />
        </Stack>

        {!statusUnavailable ? (
          <Stack
            direction="row"
            spacing={1.5}
            useFlexGap
            sx={{ flexWrap: "wrap" }}
          >
            <Typography variant="body2">
              {players.length}/{court.capacity}{" "}
              {court.occupancy_state === "in_play" ? "playing" : "occupied"}
            </Typography>
            {lineSummary ? (
              <Typography variant="body2">{lineSummary}</Typography>
            ) : null}
            {recoveredLineEntry ? (
              <Typography variant="body2">
                Your position {recoveredLineEntry.position ?? "pending"}
              </Typography>
            ) : null}
          </Stack>
        ) : null}

        {partyEligibility ? (
          <CourtPartyBlocker
            placement={court.party_placement}
            eligibility={partyEligibility}
          />
        ) : null}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
        >
          {/* Capacity/privacy is secondary detail for every live game. */}
          {!statusUnavailable && court.occupancy_state === "in_play" ? (
            <Typography variant="body2" color="text.secondary">
              {court.is_private
                ? "Private game"
                : `${court.available_slots} ${
                    court.available_slots === 1 ? "slot" : "slots"
                  } available`}
            </Typography>
          ) : (
            <Box />
          )}
          <Stack direction="row" spacing={1}>
            {!statusUnavailable ? (
              <Button
                ref={detailsButtonRef}
                type="button"
                variant="text"
                size="small"
                aria-label={`View ${court.name} details`}
                aria-expanded={detailsOpen}
                aria-controls={`court-details-${court.court_id}`}
                onClick={() => setDetailsOpen(true)}
              >
                Details
              </Button>
            ) : null}
            {action}
          </Stack>
        </Stack>
        {detailsOpen && !statusUnavailable ? (
          <CourtDetailsSheet court={court} onClose={closeDetails} />
        ) : null}
      </Stack>
    </Paper>
  );
}

/**
 * Player self-serve COURT BOARD (/dropin/:facilityId/court-board).
 *
 * Shows every non-deleted court's operation, occupancy, and player-specific
 * availability, lets a player enter a server-managed line, join a line-free live
 * game when offered, or leave the court they're on. A "Looking to play" panel
 * lists checked-in players not currently on a court.
 *
 * The backend is authoritative on join/line validity — the FE shows the
 * availability-driven action and surfaces the server's error honestly (a
 * solo-can't-start 409 → "need a party" copy; a one-spot-anywhere 409 on Join
 * line → "already have a spot" copy), and NEVER pre-computes eligibility or
 * fabricates state. No raw UID is shown anywhere (the no-UID fence degrades an
 * unresolved name to an em-dash) — including the waiting line and the "Next
 * up" marker.
 */
export function CourtBoardPage() {
  const { facilityId = "" } = useParams<{ facilityId: string }>();
  const [searchParams] = useSearchParams();
  const requestedPartyId = searchParams.get("partyId");

  return (
    <CourtBoardFacilityPage
      key={`${facilityId}:${requestedPartyId ?? ""}`}
      facilityId={facilityId}
      requestedPartyId={requestedPartyId}
    />
  );
}

function CourtBoardFacilityPage({
  facilityId,
  requestedPartyId,
}: {
  facilityId: string;
  requestedPartyId: string | null;
}) {
  const sessionQuery = useDropInSession(facilityId, {
    refreshMode: "lifecycle",
  });
  const session = sessionQuery.data ?? null;
  const activeAuthorizationExpiresAt = activeDropInAuthorizationExpiresAt(
    session,
    facilityId,
  );
  const sessionAuthorizationIsFresh =
    dropInSessionSnapshotIsAuthoritative(sessionQuery) &&
    activeAuthorizationExpiresAt !== null;
  const authorizationExpiresAt = sessionAuthorizationIsFresh
    ? activeAuthorizationExpiresAt
    : null;
  const boardQuery = useCourtBoard(facilityId, {
    enabled: sessionAuthorizationIsFresh,
    authorizationExpiresAt,
  });
  const facilityQuery = useFacilityMode(facilityId);
  const join = useJoinCourt(facilityId);
  const leave = useLeaveCourt(facilityId);
  const joinLine = useJoinLine(facilityId);
  const leaveLine = useLeaveLine(facilityId);
  const myEntryQuery = useMyQueueEntry(facilityId);

  // The nullable current-party read remains the lifecycle recovery source and
  // is refetched with conflicts. Placement itself comes only from the board's
  // PartyEligibilityResponse and each court's PartyCourtPlacement.
  const myPartyQuery = useMyParty(facilityId);
  const myParty = myPartyQuery.data ?? null;
  const board = boardQuery.data ?? null;
  const partyEligibility = board?.party_eligibility ?? null;
  const boardPartySnapshotCurrent =
    boardQuery.isSuccess &&
    boardQuery.isFetchedAfterMount &&
    !boardQuery.isFetching &&
    !boardQuery.isRevisionPending &&
    boardQuery.error === null;
  const myPartySnapshotCurrent =
    myPartyQuery.isSuccess &&
    myPartyQuery.isFetchedAfterMount &&
    !myPartyQuery.isFetching &&
    myPartyQuery.error === null;
  const partySafetySnapshotCurrent =
    boardPartySnapshotCurrent && myPartySnapshotCurrent;
  const boardPartyMatchesMyPartyIdentity =
    partyEligibility !== null &&
    myParty !== null &&
    partyEligibility.party_id === myParty.party_id &&
    partyEligibility.facility_id === facilityId &&
    myParty.facility_id === facilityId;
  const partyEligibilitySnapshotsMatch =
    board !== null &&
    partyEligibility !== null &&
    myParty !== null &&
    partyEligibility.revision === board.revision &&
    myParty.eligibility.revision === board.revision &&
    partyEligibilitySnapshotsAgree(partyEligibility, myParty.eligibility);
  const requestedPartyMatches =
    requestedPartyId === null ||
    (partyEligibility !== null &&
      requestedPartyId === partyEligibility.party_id);
  const partyContextMatchesRoute =
    boardPartyMatchesMyPartyIdentity &&
    requestedPartyMatches &&
    partyEligibilitySnapshotsMatch;
  const requestedPartyIsStale =
    requestedPartyId !== null &&
    partySafetySnapshotCurrent &&
    (!boardPartyMatchesMyPartyIdentity || !requestedPartyMatches);
  const partySnapshotsAreReconciling =
    partySafetySnapshotCurrent &&
    boardPartyMatchesMyPartyIdentity &&
    requestedPartyMatches &&
    !partyEligibilitySnapshotsMatch;
  const callerLeadsParty =
    boardPartyMatchesMyPartyIdentity &&
    myParty !== null &&
    myParty.viewer.role === "leader" &&
    myParty.viewer.can_place;
  const canLeaveCurrentLine =
    myPartySnapshotCurrent &&
    (myParty === null ||
      myEntryQuery.data?.group_id !== myParty.party_id ||
      myParty.viewer.can_place);
  const queuePartyForCourt = useQueueParty(
    partyEligibility?.party_id ?? "",
    facilityId,
  );
  const kickMember = useKickPartyMember(partyEligibility?.party_id ?? "");

  // ONE visible error channel shared by join + leave + join-line + queue-party:
  // only one action happens at a time, so a single banner is correct and a new
  // attempt clears the prior message. A failed leave can never be swallowed
  // (see handleLeave).
  const [actionError, setActionError] = useState<PartyPlacementNotice | null>(
    null,
  );
  const [pendingPlacement, setPendingPlacement] = useState<{
    courtId: string;
    entryId: string;
  } | null>(null);
  const [eligibilityFocus, setEligibilityFocus] =
    useState<EligibilityFocusRequest | null>(null);
  const focusSequence = useRef(0);
  const partyRecoveryRef = useRef<HTMLDivElement>(null);
  const actionErrorRef = useRef<HTMLDivElement>(null);
  const courtElements = useRef(new Map<string, HTMLDivElement>());
  const setPartyRecoveryRef = useCallback(
    (element: HTMLDivElement | null) => {
      partyRecoveryRef.current = element;
      if (element && eligibilityFocus?.target === "recovery") {
        element.focus();
        element.scrollIntoView?.({ block: "nearest" });
      }
    },
    [eligibilityFocus],
  );

  const boardActionsDisabled =
    !boardQuery.isSuccess ||
    boardQuery.isFetching ||
    boardQuery.isRevisionPending ||
    boardQuery.error !== null ||
    !myEntryQuery.isSuccess ||
    myEntryQuery.isFetching ||
    myEntryQuery.error !== null;
  const courts = board?.courts ?? [];
  const lookingToPlay = boardQuery.isError
    ? []
    : (board?.looking_to_play ?? []);
  const callerWasOnCourt = useRef<boolean | null>(null);
  const partyActionEnabled =
    partyContextMatchesRoute &&
    partySafetySnapshotCurrent &&
    callerLeadsParty &&
    partyEligibility !== null &&
    myParty !== null &&
    partyEligibility.action === "choose_court" &&
    partyEligibility.leader_can_place &&
    myParty.eligibility.action === "choose_court" &&
    myParty.eligibility.leader_can_place &&
    !boardActionsDisabled;

  useEffect(() => {
    if (eligibilityFocus?.target !== "recovery") {
      return;
    }
    const target = requestedPartyIsStale
      ? partyRecoveryRef.current
      : actionErrorRef.current;
    target?.focus();
    target?.scrollIntoView?.({ block: "nearest" });
  }, [actionError, eligibilityFocus, requestedPartyIsStale]);

  useEffect(() => {
    if (eligibilityFocus?.target !== "court" || !eligibilityFocus.courtId) {
      return;
    }
    const target =
      courtElements.current.get(eligibilityFocus.courtId) ??
      actionErrorRef.current;
    target?.focus();
    target?.scrollIntoView?.({ block: "nearest" });
  }, [actionError, courts, eligibilityFocus]);

  useEffect(() => {
    if (
      pendingPlacement === null ||
      boardQuery.isFetching ||
      myEntryQuery.isFetching ||
      myPartyQuery.isFetching ||
      !boardQuery.isSuccess ||
      !myEntryQuery.isSuccess ||
      !myPartyQuery.isSuccess ||
      myEntryQuery.data?.entry_id !== pendingPlacement.entryId ||
      myEntryQuery.data.target_court_id !== pendingPlacement.courtId ||
      !courts.some((court) => court.court_id === pendingPlacement.courtId)
    ) {
      return;
    }

    const affectedCourt = courtElements.current.get(pendingPlacement.courtId);
    affectedCourt?.focus();
    affectedCourt?.scrollIntoView?.({ block: "nearest" });
    setPendingPlacement(null);
  }, [
    boardQuery.isFetching,
    boardQuery.isSuccess,
    courts,
    myEntryQuery.data,
    myEntryQuery.isFetching,
    myEntryQuery.isSuccess,
    myPartyQuery.isFetching,
    myPartyQuery.isSuccess,
    pendingPlacement,
  ]);

  useEffect(() => {
    if (!board) return;
    const callerOnCourt = (board.courts ?? []).some((court) =>
      (court.players ?? []).some((player) => player.is_current_user),
    );
    const previous = callerWasOnCourt.current;
    callerWasOnCourt.current = callerOnCourt;
    if (previous === true && !callerOnCourt) {
      void myPartyQuery.refetch();
    }
  }, [board, myPartyQuery.refetch]);

  function handleJoin(courtId: string) {
    if (boardActionsDisabled) return;
    setActionError(null);
    join.mutate(courtId, {
      onError: (err) => setActionError({ message: mapJoinError(err) }),
    });
  }

  function handleLeave(courtId: string) {
    if (boardActionsDisabled) return;
    setActionError(null);
    leave.mutate(courtId, {
      // A failed Leave must surface honest feedback — never silence. (The board
      // is NOT optimistically wiped on failure: the court persists because the
      // board only re-reads on a SUCCESSFUL leave's invalidation.)
      onError: (err) => setActionError({ message: mapLeaveError(err) }),
    });
  }

  function handleJoinLine(courtId: string) {
    if (boardActionsDisabled) return;
    setActionError(null);
    joinLine.mutate(courtId, {
      onError: (err) => setActionError({ message: mapJoinLineError(err) }),
    });
  }

  function handleLeaveLine(entryId: string) {
    if (boardActionsDisabled) return;
    setActionError(null);
    leaveLine.mutate(entryId, {
      onError: () => setActionError({ message: LEAVE_LINE_FAILED_COPY }),
    });
  }

  function handleQueueParty(courtId: string) {
    if (boardActionsDisabled || !partyActionEnabled || !partyEligibility) {
      return;
    }
    const previousEligibility = partyEligibility;
    const previousCourt = courts.find((court) => court.court_id === courtId);
    setActionError(null);
    queuePartyForCourt.mutate(
      { mode: "normal", targetCourtId: courtId },
      {
        onSuccess: (entry) =>
          setPendingPlacement({ courtId, entryId: entry.entry_id }),
        onError: async (err) => {
          const status = (err as { status?: number } | null)?.status;
          if (status === 409) {
            const conflictNotice = partyPlacementConflictNotice(err);
            const [partyResult, boardResult, entryResult] = await Promise.all([
              myPartyQuery.refetch(),
              boardQuery.refetch(),
              myEntryQuery.refetch(),
            ]);
            const partyDisappeared =
              partyResult.isSuccess && partyResult.data === null;
            const nextEligibility = partyDisappeared
              ? null
              : boardResult.isSuccess
                ? (boardResult.data?.party_eligibility ?? null)
                : partyResult.isSuccess
                  ? partyResult.data?.eligibility
                  : previousEligibility;
            const eligibilityChanged = hasPartyEligibilityDisplayChange(
              previousEligibility,
              nextEligibility,
            );
            const nextCourt = boardResult.isSuccess
              ? (boardResult.data?.courts ?? []).find(
                  (court) => court.court_id === courtId,
                )
              : undefined;
            setEligibilityFocus(
              !eligibilityChanged &&
                courtPlacementStateChanged(previousCourt, nextCourt)
                ? {
                    key: ++focusSequence.current,
                    target: "court",
                    courtId,
                  }
                : createEligibilityFocusRequest(
                    ++focusSequence.current,
                    previousEligibility,
                    nextEligibility,
                  ),
            );
            if (
              partyResult.isSuccess &&
              boardResult.isSuccess &&
              entryResult.isSuccess
            ) {
              setActionError(conflictNotice);
            } else {
              setActionError({
                ...conflictNotice,
                message: `${conflictNotice.message} We couldn't refresh every status.`,
              });
            }
            return;
          }
          setActionError({ message: mapQueuePartyError(err) });
        },
      },
    );
  }

  function handleKickPartyMember(targetMemberId: string) {
    if (
      !partyContextMatchesRoute ||
      !partySafetySnapshotCurrent ||
      myParty?.viewer.can_remove_members !== true ||
      boardActionsDisabled
    ) {
      return;
    }
    setActionError(null);
    kickMember.mutate(targetMemberId, {
      onSuccess: () => {
        void Promise.all([boardQuery.refetch(), myPartyQuery.refetch()]);
      },
      onError: () =>
        setActionError({
          message: "We couldn't remove that member. Please try again.",
        }),
    });
  }

  return (
    <Page>
      <Stack
        spacing={3}
        sx={{ "& .MuiButton-root.Mui-disabled": { pointerEvents: "auto" } }}
      >
        <Box>
          <DetailHeader
            title="Court board"
            titleComponent="p"
            subtitle={
              [
                facilityQuery.data?.name,
                courts.length > 0
                  ? `${courts.length} ${courts.length === 1 ? "court" : "courts"}`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ") || undefined
            }
            back={{ to: `/dropin/${facilityId}/session`, label: "Back to session" }}
          />
          <Typography component="h1" sx={visuallyHidden}>
            Courts right now
          </Typography>
        </Box>

        {partyContextMatchesRoute && partyEligibility ? (
          <PartyEligibilityPanel
            eligibility={partyEligibility}
            canPlaceParty={myParty?.viewer.can_place === true}
            canRemoveMembers={myParty?.viewer.can_remove_members === true}
            canViewActivity={myParty?.viewer.can_leave === true}
            actionsDisabled={
              boardActionsDisabled || !partySafetySnapshotCurrent
            }
            actionPending={queuePartyForCourt.isPending}
            removePending={kickMember.isPending}
            showPartyAction={false}
            partyHref={`/dropin/party/${partyEligibility.party_id}`}
            partyHrefLabel="Back to party"
            focusRequest={eligibilityFocus}
            onRemoveMember={
              callerLeadsParty ? handleKickPartyMember : undefined
            }
          />
        ) : null}

        {actionError ? (
          <Alert
            ref={actionErrorRef}
            severity="error"
            data-conflict-code={actionError.conflictCode}
            tabIndex={-1}
          >
            {actionError.message}
          </Alert>
        ) : null}

        {partySnapshotsAreReconciling ? (
          <Alert severity="error">
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ alignItems: { sm: "center" } }}
            >
              <Typography sx={{ flex: 1 }}>
                Party and court status are still refreshing. Placement is paused
                until both show the same update.
              </Typography>
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={() => {
                  void Promise.all([
                    boardQuery.refetch(),
                    myPartyQuery.refetch(),
                  ]);
                }}
              >
                Refresh status
              </Button>
            </Stack>
          </Alert>
        ) : null}

        {myPartyQuery.error !== null ? (
          <Alert severity="error">
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ alignItems: { sm: "center" } }}
            >
              <Typography sx={{ flex: 1 }}>
                Party status is unavailable. Court placement is paused until it
                is current.
              </Typography>
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={() => void myPartyQuery.refetch()}
              >
                Retry party status
              </Button>
            </Stack>
          </Alert>
        ) : null}

        {requestedPartyIsStale ? (
          <Alert ref={setPartyRecoveryRef} severity="error" tabIndex={-1}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ alignItems: { sm: "center" } }}
            >
              <Typography sx={{ flex: 1 }}>
                {myParty === null
                  ? "This party is no longer active or available for placement."
                  : "This court link belongs to a different party."}
              </Typography>
              {myParty !== null ? (
                <Button
                  component={Link}
                  to={`/dropin/party/${myParty.party_id}`}
                  variant="outlined"
                  size="small"
                >
                  View current party
                </Button>
              ) : null}
            </Stack>
          </Alert>
        ) : null}

        {boardQuery.isError ? (
          <Alert severity="error">
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ alignItems: { sm: "center" } }}
            >
              <Typography sx={{ flex: 1 }}>
                {board === null
                  ? "We couldn't load the court board. Please try again."
                  : "Court status is unavailable because the board couldn't refresh. Actions are disabled until it updates."}
              </Typography>
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={() => void boardQuery.refetch()}
              >
                Retry court status
              </Button>
            </Stack>
          </Alert>
        ) : null}

        {myEntryQuery.error !== null ? (
          <Alert severity="error">
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ alignItems: { sm: "center" } }}
            >
              <Typography sx={{ flex: 1 }}>
                Your queue status is unavailable. Court actions are paused until
                it is current.
              </Typography>
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={() => void myEntryQuery.refetch()}
              >
                Retry queue status
              </Button>
            </Stack>
          </Alert>
        ) : null}

        {boardQuery.isLoading ? (
          <Stack
            direction="row"
            spacing={1}
            role="status"
            aria-live="polite"
            sx={{ alignItems: "center" }}
          >
            <CircularProgress size={18} />
            <Typography color="text.secondary">Loading the courts…</Typography>
          </Stack>
        ) : board === null ? (
          boardQuery.error === null ? (
            <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
              <Typography color="text.secondary">
                The court board opens once you&apos;re checked in for drop-in.
              </Typography>
              <Button
                component={Link}
                to={`/dropin/${facilityId}`}
                variant="contained"
                size="small"
              >
                Check in
              </Button>
            </Stack>
          ) : null
        ) : (
          <Stack spacing={3}>
            {/* Courts */}
            <Box component="section" aria-labelledby="sec-courts">
              <SectionHeading eyebrow="Drop-in" id="sec-courts">
                Courts
              </SectionHeading>
              {courts.length === 0 ? (
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  No courts are active right now. Check back when a game gets
                  going.
                </Typography>
              ) : (
                <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                  {courts.map((court) => (
                    <CourtCard
                      key={court.court_id}
                      court={court}
                      callerLineEntry={myEntryQuery.data ?? null}
                      onJoin={handleJoin}
                      onLeave={handleLeave}
                      onLeaveLine={handleLeaveLine}
                      onJoinLine={handleJoinLine}
                      joinPending={join.isPending}
                      leavePending={leave.isPending}
                      leaveLinePending={leaveLine.isPending}
                      joinLinePending={joinLine.isPending}
                      partyEligibility={
                        partyContextMatchesRoute ? partyEligibility : null
                      }
                      partyActionEnabled={partyActionEnabled}
                      onQueueParty={handleQueueParty}
                      queuePartyPending={queuePartyForCourt.isPending}
                      actionsDisabled={boardActionsDisabled}
                      statusUnavailable={boardQuery.isError}
                      canLeaveLine={canLeaveCurrentLine}
                      onCourtRef={(element) => {
                        if (element) {
                          courtElements.current.set(court.court_id, element);
                        } else {
                          courtElements.current.delete(court.court_id);
                        }
                      }}
                      suppressSoloPlacement={
                        requestedPartyId !== null ||
                        myParty !== null ||
                        partyEligibility !== null
                      }
                      soloPlacementDisabled={!partySafetySnapshotCurrent}
                    />
                  ))}
                </Stack>
              )}
            </Box>

            {/* Open to play — checked-in players not currently on a court. The
              heading reads "Open to play" (not "Looking to play") so it never
              collides with the empty-state's "No one is looking to play" copy
              when both could otherwise match a who's-here text query. */}
            <Box component="section" aria-labelledby="sec-ltp">
              <SectionHeading eyebrow="Here now" id="sec-ltp">
                Open to play
              </SectionHeading>
              {boardQuery.isError ? (
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  Open-to-play status unavailable.
                </Typography>
              ) : lookingToPlay.length === 0 ? (
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  No one is looking to play right now.
                </Typography>
              ) : (
                <Stack
                  component="ul"
                  spacing={1}
                  sx={{ mt: 1.5, p: 0, listStyle: "none" }}
                >
                  {lookingToPlay.map((p) => (
                    <Paper
                      component="li"
                      variant="outlined"
                      key={p.presence_id}
                      sx={{ p: 1.5 }}
                    >
                      <PlayerName player={p} />
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        )}
      </Stack>
    </Page>
  );
}

function SectionHeading({
  eyebrow,
  id,
  children,
}: {
  eyebrow: string;
  id: string;
  children: string;
}) {
  return (
    <Box>
      <Typography variant="overline" color="primary.main">
        {eyebrow}
      </Typography>
      <Typography component="h2" variant="h5" id={id}>
        {children}
      </Typography>
    </Box>
  );
}
